import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { copyToClipboard } from '../utils/clipboard';

/* ===========================================================================
 * CheckoutIntegrations — tela genérica de integração com checkouts externos
 * (Appmax, Stripe, Luna, Vega, Adoorei, Zedy, Corvex) via webhook.
 *
 * Backend: /api/checkouts/{integrations, integrations/connect, integrations/:id,
 *          orders, orders/:id/generate, integrations/:id/logs}
 *
 * Segue o padrão visual compartilhado .ipg-* (ver integration-pages.css +
 * darkmode.css) — o mesmo usado pela tela de Webhooks de Saída (notificações).
 * ========================================================================= */

// Logo real de cada plataforma via favicon oficial do domínio (mesma técnica
// usada por Zapier/Make pra exibir ícones de integrações de terceiros sem
// precisar hospedar o arquivo). Se a imagem falhar, cai pro ícone genérico.
const favicon = (domain) => `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;

const CHECKOUT_META = {
  appmax: { label: 'Appmax', icon: 'fa-bolt', color: '#111', logoUrl: favicon('appmax.com.br') },
  stripe: { label: 'Stripe', icon: 'fa-stripe', iconLib: 'fab', color: '#635bff' },
  luna: { label: 'Luna Checkout', icon: 'fa-moon', color: '#0ea5e9', logoUrl: favicon('lunacheckout.com') },
  vega: { label: 'Vega Checkout', icon: 'fa-meteor', color: '#7c3aed', logoUrl: favicon('vegacheckout.com.br') },
  adoorei: { label: 'Adoorei', icon: 'fa-heart', color: '#ec4899', logoUrl: favicon('adoorei.com.br') },
  zedy: { label: 'Zedy', icon: 'fa-bolt-lightning', color: '#22c55e' },
  corvex: { label: 'Corvex', icon: 'fa-globe', color: '#f59e0b' },
};

const ROUTE_OPTIONS = [
  { value: 'BR', label: 'BR — Nacional Brasil' },
  { value: 'CN', label: 'CN — China → Brasil' },
  { value: 'INT', label: 'INT — Internacional' },
];

/** Ícone do tipo de checkout: usa o logo real (favicon oficial) quando disponível,
 * e cai pro ícone genérico automaticamente se a imagem falhar ao carregar. */
const TypeIcon = ({ meta }) => {
  const [failed, setFailed] = useState(false);
  if (meta.logoUrl && !failed) {
    return (
      <div className="ipg-type-icon" style={{ background: '#fff' }}>
        <img src={meta.logoUrl} alt={meta.label} onError={() => setFailed(true)} style={{ width: 22, height: 22, objectFit: 'contain' }} />
      </div>
    );
  }
  return (
    <div className="ipg-type-icon" style={{ background: meta.color }}>
      <i className={`${meta.iconLib || 'fas'} ${meta.icon}`}></i>
    </div>
  );
};

const CopyBtn = ({ value }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    const ok = await copyToClipboard(value);
    if (ok) { setCopied(true); setTimeout(() => setCopied(false), 1500); }
  };
  return (
    <button type="button" className="ipg-copy-btn" onClick={onCopy} title="Copiar">
      <i className={`fas ${copied ? 'fa-check' : 'fa-copy'}`}></i>
    </button>
  );
};

const CheckoutIntegrations = () => {
  const { user } = useAuth();
  const credits = user?.credits ?? 0;

  const [data, setData] = useState({ integrations: [], checkoutTypes: {}, maxPerType: 3 });
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [connectingType, setConnectingType] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [ordersByIntegration, setOrdersByIntegration] = useState({});
  const [logsByIntegration, setLogsByIntegration] = useState({});

  const showToast = useCallback((type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const [orphanedPending, setOrphanedPending] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, ordersRes] = await Promise.all([
        api.get('/checkouts/integrations'),
        api.get('/checkouts/orders'),
      ]);
      setData(r.data);
      // Pedidos pendentes cuja loja já foi desconectada (apagada) — sem essa
      // visão, o cliente nunca mais conseguiria ver/gerar código pra eles.
      const liveIds = new Set((r.data.integrations || []).map((i) => i.id));
      const orphans = (ordersRes.data.orders || []).filter((o) => o.status !== 'success' && !liveIds.has(o.integration_id));
      setOrphanedPending(orphans);
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Erro ao carregar integrações');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const typesToShow = Object.keys(CHECKOUT_META);

  const integrationsByType = typesToShow.reduce((acc, t) => {
    acc[t] = data.integrations.filter((i) => i.checkout_type === t);
    return acc;
  }, {});

  const toggleExpand = async (integration) => {
    if (expandedId === integration.id) { setExpandedId(null); return; }
    setExpandedId(integration.id);
    try {
      const [ordersRes, logsRes] = await Promise.all([
        api.get('/checkouts/orders', { params: { integrationId: integration.id } }),
        api.get(`/checkouts/integrations/${integration.id}/logs`),
      ]);
      setOrdersByIntegration((prev) => ({ ...prev, [integration.id]: ordersRes.data.orders || [] }));
      setLogsByIntegration((prev) => ({ ...prev, [integration.id]: logsRes.data.logs || [] }));
    } catch (err) {
      showToast('error', 'Erro ao carregar detalhes');
    }
  };

  const handleDisconnect = async (integration) => {
    const pendingCount = (ordersByIntegration[integration.id] || []).filter((o) => o.status !== 'success').length;
    const warning = pendingCount > 0
      ? `\n\n⚠️ Atenção: ${pendingCount} pedido(s) pago(s) ainda SEM código gerado. Depois de desconectar, você NÃO poderá mais gerar código pra eles — gere agora antes de desconectar, se quiser.`
      : '';
    if (!window.confirm(`Desconectar "${integration.store_name || integration.checkout_type}"? O histórico de pedidos/códigos já gerados é mantido pra sempre.${warning}`)) return;
    try {
      await api.delete(`/checkouts/integrations/${integration.id}`);
      showToast('success', 'Integração desconectada');
      load();
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Erro ao desconectar');
    }
  };

  const handleGenerate = async (orderId, integrationId) => {
    try {
      const r = await api.post(`/checkouts/orders/${orderId}/generate`);
      showToast('success', `Código gerado: ${r.data.trackingCode}`);
      const ordersRes = await api.get('/checkouts/orders', { params: { integrationId } });
      setOrdersByIntegration((prev) => ({ ...prev, [integrationId]: ordersRes.data.orders || [] }));
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Erro ao gerar código');
    }
  };

  const handleGenerateOrphan = async (orderId) => {
    try {
      const r = await api.post(`/checkouts/orders/${orderId}/generate`);
      showToast('success', `Código gerado: ${r.data.trackingCode}`);
      load();
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Erro ao gerar código');
    }
  };

  const handleToggleAuto = async (intg) => {
    try {
      await api.put(`/checkouts/integrations/${intg.id}`, { autoGenerate: !intg.auto_generate });
      showToast('success', !intg.auto_generate
        ? 'Geração automática ativada — próximas vendas aprovadas já geram o código sozinhas'
        : 'Geração automática desativada — pedidos ficarão pendentes pra você gerar manualmente');
      load();
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Erro ao atualizar');
    }
  };

  const handleGenerateAll = async (intg) => {
    try {
      const r = await api.post(`/checkouts/integrations/${intg.id}/generate-pending`);
      if (r.data.generated === 0 && r.data.skipped === 0) {
        showToast('success', 'Nenhum pedido pendente');
      } else if (r.data.stoppedForCredit) {
        showToast('error', `${r.data.generated} código(s) gerado(s), mas o saldo acabou — ${r.data.skipped} pedido(s) continuam pendentes`);
      } else {
        showToast('success', `${r.data.generated} código(s) gerado(s) com sucesso!`);
      }
      const ordersRes = await api.get('/checkouts/orders', { params: { integrationId: intg.id } });
      setOrdersByIntegration((prev) => ({ ...prev, [intg.id]: ordersRes.data.orders || [] }));
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Erro ao gerar códigos em lote');
    }
  };

  return (
    <>
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.2/css/all.min.css" />
      <link href="https://fonts.googleapis.com/css2?family=Lexend:wght@100..900&display=swap" rel="stylesheet" />

      <div className="ipg">
        <header className="ipg-header">
          <div className="ipg-header-inner">
            <div>
              <h1 className="ipg-header-title"><i className="fas fa-plug"></i> Integrações de Checkout</h1>
              <p className="ipg-header-sub">Conecte seus checkouts via webhook e gere rastreios automaticamente</p>
            </div>
            <div className={`ipg-balance-chip ${credits === 0 ? 'empty' : credits < 10 ? 'low' : ''}`}>
              <i className="fas fa-coins"></i>
              <div className="ipg-balance-info">
                <span className="ipg-balance-label">Créditos</span>
                <span className="ipg-balance-val">{credits}</span>
              </div>
            </div>
          </div>
        </header>

        {toast && (
          <div className={`ipg-toast ipg-toast-${toast.type}`}>
            <i className={`fas ${toast.type === 'success' ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
            <span>{toast.msg}</span>
          </div>
        )}

        <main className="ipg-main">
          {!loading && orphanedPending.length > 0 && (
            <section className="ipg-type-card" style={{ borderColor: 'rgba(245,158,11,0.4)' }}>
              <h4 style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <i className="fas fa-triangle-exclamation" style={{ color: '#f59e0b' }}></i>
                Pedidos de lojas já desconectadas ({orphanedPending.length})
              </h4>
              <p className="ipg-empty-mini" style={{ textAlign: 'left', padding: 0, marginBottom: 10 }}>
                Esses pedidos foram pagos antes de você desconectar a loja de origem, e ainda não têm código. Você ainda pode gerar.
              </p>
              {orphanedPending.map((o) => (
                <div key={o.id} className="ipg-order-row">
                  <div>
                    <strong>{o.external_order_id}</strong> — {o.customer_name || 'Cliente'} <small>({o.checkout_type})</small>
                    <div className="ipg-order-dest">{o.destination}</div>
                  </div>
                  <div className="ipg-order-status">
                    <span className="ipg-tag yellow">{o.error_message || 'Aguardando'}</span>
                    <button className="ipg-btn-mini" onClick={() => handleGenerateOrphan(o.id)}>Gerar código</button>
                  </div>
                </div>
              ))}
            </section>
          )}
          {loading ? (
            <div className="ipg-loading"><div className="ipg-spinner"></div> Carregando…</div>
          ) : (
            typesToShow.map((type) => {
              const meta = CHECKOUT_META[type];
              const list = integrationsByType[type] || [];
              const isConnecting = connectingType === type;
              return (
                <section key={type} className="ipg-type-card">
                  <div className="ipg-type-head">
                    <TypeIcon meta={meta} />
                    <div className="ipg-type-info">
                      <strong>{meta.label}</strong>
                      <span>{list.length}/{data.maxPerType} conectadas</span>
                    </div>
                    {list.length < data.maxPerType && (
                      <button className="ipg-btn-add" onClick={() => setConnectingType(isConnecting ? null : type)}>
                        <i className="fas fa-plus"></i> Conectar
                      </button>
                    )}
                  </div>

                  {isConnecting && (
                    <ConnectForm
                      checkoutType={type}
                      requiresToken={data.checkoutTypes[type]?.requiresToken}
                      onCancel={() => setConnectingType(null)}
                      onConnected={() => { setConnectingType(null); load(); showToast('success', `${meta.label} conectado!`); }}
                      showToast={showToast}
                    />
                  )}

                  {list.length > 0 && (
                    <div className="ipg-store-list">
                      {list.map((intg) => (
                        <div key={intg.id} className="ipg-store-row">
                          <div className="ipg-store-row-head" onClick={() => toggleExpand(intg)}>
                            <span className={`ipg-health-dot ${!intg.active ? 'off' : intg.last_error_type ? 'error' : 'ok'}`}></span>
                            <span className="ipg-store-name">{intg.store_name || `Integração #${intg.id}`}</span>
                            <span className={`ipg-badge ${intg.active ? 'on' : 'off'}`}>{intg.active ? 'Ativa' : 'Desconectada'}</span>
                            <i className={`fas fa-chevron-${expandedId === intg.id ? 'up' : 'down'}`}></i>
                          </div>

                          {expandedId === intg.id && (
                            <div className="ipg-store-details">
                              <div className="ipg-field-row">
                                <label>URL do webhook</label>
                                <div className="ipg-url-box">
                                  <code>{intg.webhookUrl}</code>
                                  <CopyBtn value={intg.webhookUrl} />
                                </div>
                                <small>Cole essa URL no painel de webhooks do {CHECKOUT_META[type].label}.</small>
                              </div>
                              <div className="ipg-field-row-inline">
                                <span><i className="fas fa-map-pin"></i> Origem: <b>{intg.default_origin || '—'}</b></span>
                                <span><i className="fas fa-route"></i> Rota: <b>{intg.route_type}</b></span>
                                {intg.last_webhook_at && <span><i className="fas fa-clock"></i> Último webhook: {new Date(intg.last_webhook_at).toLocaleString('pt-BR')}</span>}
                              </div>
                              <div
                                className="ipg-toggle"
                                onClick={(e) => { e.stopPropagation(); handleToggleAuto(intg); }}
                                title={intg.auto_generate
                                  ? 'Desligar: pedidos ficam pendentes pra você gerar quando quiser'
                                  : 'Ligar: toda venda aprovada já gera o código sozinha, sem você precisar clicar'}
                              >
                                <span className={`ipg-toggle-track ${intg.auto_generate ? 'on' : ''}`}>
                                  <span className="ipg-toggle-knob"></span>
                                </span>
                                <span>
                                  <i className="fas fa-robot"></i> Gerar automaticamente: <b>{intg.auto_generate ? 'Ligado' : 'Desligado'}</b>
                                  {' '}— {intg.auto_generate ? 'toda venda aprovada já gera o código sozinha' : 'você escolhe quando gerar'}
                                </span>
                              </div>

                              {intg.active && (
                                <button className="ipg-btn-danger" onClick={() => handleDisconnect(intg)}>
                                  <i className="fas fa-link-slash"></i> Desconectar
                                </button>
                              )}

                              <div className="ipg-section">
                                <div className="ipg-section-head">
                                  <h4><i className="fas fa-cart-shopping"></i> Pedidos ({(ordersByIntegration[intg.id] || []).length})</h4>
                                  {(ordersByIntegration[intg.id] || []).filter((o) => o.status !== 'success').length > 1 && (
                                    <button className="ipg-btn-batch" onClick={() => handleGenerateAll(intg)}>
                                      <i className="fas fa-bolt"></i> Gerar todos pendentes ({(ordersByIntegration[intg.id] || []).filter((o) => o.status !== 'success').length})
                                    </button>
                                  )}
                                </div>
                                {(ordersByIntegration[intg.id] || []).length === 0 ? (
                                  <p className="ipg-empty-mini">Nenhum pedido recebido ainda.</p>
                                ) : (
                                  (ordersByIntegration[intg.id] || []).map((o) => (
                                    <div key={o.id} className="ipg-order-row">
                                      <div>
                                        <strong>{o.external_order_id}</strong> — {o.customer_name || 'Cliente'}
                                        <div className="ipg-order-dest">{o.destination}</div>
                                      </div>
                                      <div className="ipg-order-status">
                                        {o.status === 'success' ? (
                                          <span className="ipg-tag green"><i className="fas fa-check"></i> {o.tracking_code}</span>
                                        ) : (
                                          <>
                                            <span className="ipg-tag yellow">{o.error_message || 'Aguardando'}</span>
                                            <button className="ipg-btn-mini" onClick={() => handleGenerate(o.id, intg.id)}>Gerar código</button>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  ))
                                )}
                              </div>

                              <div className="ipg-section">
                                <h4><i className="fas fa-list"></i> Log de diagnóstico (últimos eventos)</h4>
                                {(logsByIntegration[intg.id] || []).length === 0 ? (
                                  <p className="ipg-empty-mini">Nenhum evento recebido ainda — conecte e aguarde o primeiro webhook de teste.</p>
                                ) : (
                                  (logsByIntegration[intg.id] || []).slice(0, 15).map((log) => (
                                    <div key={log.id} className={`ipg-log-row ${log.success ? 'ok' : 'error'}`}>
                                      <span className="ipg-log-time">{new Date(log.created_at).toLocaleString('pt-BR')}</span>
                                      <span className="ipg-log-event">{log.event_type || '—'}</span>
                                      <span className="ipg-log-msg">{log.error_message || (log.success ? 'OK' : '—')}</span>
                                    </div>
                                  ))
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              );
            })
          )}
        </main>
      </div>
    </>
  );
};

/* ===================================================================== */
const ConnectForm = ({ checkoutType, requiresToken, onCancel, onConnected, showToast }) => {
  const [storeName, setStoreName] = useState('');
  const [defaultOrigin, setDefaultOrigin] = useState('');
  const [routeType, setRouteType] = useState('BR');
  const [authToken, setAuthToken] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (requiresToken && !authToken.trim()) {
      showToast('error', 'Cole o token/chave desse checkout pra continuar');
      return;
    }
    if (!defaultOrigin.trim()) {
      showToast('error', 'Defina uma origem pra essa integração');
      return;
    }
    setSaving(true);
    try {
      await api.post('/checkouts/integrations/connect', {
        checkoutType, storeName: storeName.trim(), routeType, defaultOrigin: defaultOrigin.trim(),
        authToken: authToken.trim() || undefined,
      });
      onConnected();
    } catch (err) {
      showToast('error', err.response?.data?.error || 'Erro ao conectar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="ipg-connect-form" onSubmit={submit}>
      <div className="ipg-field">
        <label>Nome da loja (apelido, opcional)</label>
        <input value={storeName} onChange={(e) => setStoreName(e.target.value)} placeholder="Ex: Loja BR" />
      </div>
      <div className="ipg-field">
        <label>Origem</label>
        <input value={defaultOrigin} onChange={(e) => setDefaultOrigin(e.target.value)} placeholder="Ex: São Paulo, SP" />
        <small>Origem fixa usada em todos os códigos gerados por essa integração.</small>
      </div>
      <div className="ipg-field">
        <label>Rota</label>
        <select value={routeType} onChange={(e) => setRouteType(e.target.value)}>
          {ROUTE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      {requiresToken && (
        <div className="ipg-field">
          <label>Token / Chave (copie do painel do checkout)</label>
          <input value={authToken} onChange={(e) => setAuthToken(e.target.value)} placeholder="Cole aqui" />
        </div>
      )}
      <div className="ipg-form-actions">
        <button type="button" className="ipg-btn-ghost" onClick={onCancel}>Cancelar</button>
        <button type="submit" className="ipg-btn-primary" disabled={saving}>
          {saving ? 'Gerando chave…' : 'Gerar chave e conectar'}
        </button>
      </div>
    </form>
  );
};

export default CheckoutIntegrations;
